// Demo-mode dashboard shell. Identical visuals to DashboardLayout, but the
// nav targets /demo/* and there is no real auth/sign-out.

import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Target,
  Users,
  Globe,
  DollarSign,
  Settings,
  LifeBuoy,
  LogOut,
} from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Progress } from '@/components/ui/Progress';
import { DashboardBackground } from '@/components/three';
import { PLAN_CONFIGS } from '@shared/types';
import { useDemoState } from './DemoStateProvider';

const navigation = [
  { name: 'Dashboard', href: '/demo', icon: LayoutDashboard, end: true },
  { name: 'Campaigns', href: '/demo/campaigns', icon: Target },
  { name: 'Leads', href: '/demo/leads', icon: Users },
  { name: 'Sites', href: '/demo/sites', icon: Globe },
  { name: 'Earnings', href: '/demo/earnings', icon: DollarSign },
  { name: 'Settings', href: '/demo/settings', icon: Settings },
  { name: 'Support', href: '/demo/support', icon: LifeBuoy },
];

export function DemoLayout() {
  const { userData } = useDemoState();
  const navigate = useNavigate();

  const quotaUsed = userData.usage.sitesUsed;
  const quotaLimit = userData.quotas.sitesLimit;
  const quotaPercentage = Math.min((quotaUsed / quotaLimit) * 100, 100);

  return (
    <DashboardBackground>
      <div style={{ display: 'flex', minHeight: '100vh', paddingTop: '0' }}>
        <div
          style={{
            width: '288px',
            minWidth: '288px',
            backgroundColor: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(20px)',
            borderRight: '1px solid rgba(91,58,58,0.15)',
            display: 'flex',
            flexDirection: 'column',
            position: 'sticky',
            top: '0',
            height: '100vh',
            zIndex: 20,
          }}
        >
          <div className="flex flex-col h-full">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg border-2 border-white bg-black flex items-center justify-center overflow-hidden">
                  <img src="/wolfromulus.png" alt="Romulus" className="w-8 h-8 object-contain" />
                </div>
                <h1 className="text-2xl font-bold text-white font-roman tracking-wider">ROMVLVS</h1>
              </div>
            </div>

            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all',
                      isActive
                        ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white',
                    )
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </NavLink>
              ))}
            </nav>

            <div className="p-4 border-t border-white/10">
              <div className="glass-card p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Sites Created</span>
                  <span className="text-sm font-medium text-white">
                    {quotaUsed}/{quotaLimit}
                  </span>
                </div>
                <Progress value={quotaPercentage} />
                <p className="text-xs text-gray-500 mt-2">
                  {PLAN_CONFIGS[userData.planId].name} Plan
                </p>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                <Avatar>
                  <AvatarFallback>{getInitials(userData.fullName)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{userData.fullName}</p>
                  <p className="text-xs text-gray-500 truncate">{userData.companyName}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                  <LogOut className="w-4 h-4 text-gray-400" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="animate-page-in" style={{ flex: 1, padding: '24px', position: 'relative', zIndex: 10 }}>
          <Outlet />
        </div>
      </div>
    </DashboardBackground>
  );
}
